// The one place that reads, validates, applies and stores the interface
// language. Both the switcher in the profile menu and the startup in main.ts
// go through here, so there is a single definition of what a valid choice is.

import i18n, { SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from '@/i18n'
import { useFilterStore } from '@/stores/filter.store'

/**
 * Key in the filter store. Convention of this project: <AREA>__<name>
 * (see ARTICLE__per_page, CUSTOMER__show_id).
 */
export const LOCALE_STORE_KEY = 'APP__locale'

/**
 * A stored value is only accepted when it is one of the locales this project
 * maintains. The store survives reloads and updates, so it can still hold a
 * code from an older build ('ir', 'cn') or something that is not a locale at
 * all — the same reason ArticleOverview.vue checks its stored per_page against
 * [10, 50, 100] instead of trusting it.
 */
export const isSupportedLocale = (value: unknown): value is SupportedLocale =>
  typeof value === 'string' && SUPPORTED_LOCALES.some((entry) => entry.code === value)

/**
 * The locale currently in effect.
 *
 * ⚠️ Both shapes are handled, as in the accessor in alino/sync/BathValues.vue:
 * this application creates its i18n with `legacy: false`, where `locale` is a
 * ref rather than a plain string. Reading `.value` inside a computed keeps the
 * dependency, so a template using this re-renders on a language change.
 */
export const getLocale = (): string => {
  const value = (i18n.global as any)?.locale

  if (typeof value === 'string') return value
  if (value && typeof value.value === 'string') return value.value

  return DEFAULT_LOCALE
}

/**
 * The language to read MAINTAINED CONTENT in — a recipe title, a step
 * instruction, a chemical name — as opposed to the language of the interface.
 *
 * ⚠️ THE TWO ARE NOT THE SAME SET, AND THAT IS A TRAP, NOT A SUBTLETY. The
 * interface locale is a messages file: 'de' or 'gb'. Content is stored per
 * language under the codes the editor offers, and those are 'de', 'en' and 'pl'
 * (`components/multi-lang-input/MultiLangInput.vue`, `default: () => ['de',
 * 'en', 'pl']`) — there is no 'pl' interface and there is no 'gb' content.
 *
 * So indexing a content object with the interface locale is wrong in both
 * directions, and English is where it shows:
 *
 *   name['gb']                        → undefined, and a template that
 *                                       interpolates it prints the word
 *                                       "undefined" next to the formula
 *   name['gb'] || name['de']          → German, on a screen the user has just
 *                                       switched to English, EVEN WHEN an
 *                                       English name is maintained
 *
 * The first is a defect. The second is the milder half: correct, but it hides
 * content that exists. Both disappear once the interface locale is mapped onto
 * the content locale here, in ONE place, instead of being guarded per template.
 *
 * ⚠️ The fallback stays necessary even so. A chemical maintained only in German
 * has no 'en' value, so a caller still needs `?? content['de']` behind this —
 * the established shape is `chemName()` in
 * `alino/recipe/components/recipe/components/ShowRecipe.vue`. This function
 * decides WHICH language to ask for; it does not promise the value is there.
 *
 * @returns the content language code: 'en' for the English interface, else 'de'
 */
export const contentLocale = (): string => (getLocale() === 'gb' ? 'en' : 'de')

/**
 * One multilingual value — a name, a title, a hazard code — read in the
 * language the interface is running in.
 *
 * The fallback chain is the one `chemName()` already uses in
 * `alino/recipe/components/recipe/components/ShowRecipe.vue`: the asked-for
 * language, then German, then whatever language the record does carry. The last
 * step is there because a record maintained only in Polish still HAS a value,
 * and an empty select entry is worse than a value in the wrong language.
 *
 * ⚠️ It lives here rather than as a method in each view because the three
 * templates that needed it wrote the lookup three different ways, and two of
 * them printed the word "undefined". `chemName()` and `chemical_label()` in the
 * show views are older copies of the same idea; they already guard against a
 * missing value and are therefore not defects, but they should fold into this
 * when somebody next touches them.
 *
 * @param map - a value per language code, as the editor stores it
 * @param fallback - what to return when the record carries no value at all
 * @returns the value in the content locale, or the fallback
 */
export const contentText = (map: unknown, fallback = '—'): string => {
  if (!map || typeof map !== 'object') return fallback

  const values = map as Record<string, unknown>

  // ⚠️ EACH STEP OF THE CHAIN MUST SKIP AN EMPTY VALUE, not only a missing one.
  // `values[contentLocale()] ?? values['de']` looks equivalent and is not: `??`
  // passes an empty string through, so a record whose English field was left
  // blank — which is what the editor stores for a language nobody filled in —
  // would end the chain at '' and display the fallback, although the German
  // name is right there. Found by the edge-case run, not by reading it.
  return (
    [values[contentLocale()], values['de'], ...Object.values(values)].find(
      (value): value is string => typeof value === 'string' && value !== '',
    ) ?? fallback
  )
}

const writeLocaleToI18n = (code: SupportedLocale) => {
  const target = i18n.global as any

  if (target?.locale && typeof target.locale === 'object' && 'value' in target.locale) {
    target.locale.value = code
  } else if (target) {
    target.locale = code
  }
}

/**
 * The language stored for this user, or null when there is nothing usable.
 *
 * Wrapped in try/catch although the filter store is the established pattern:
 * its own getPerists() reads localStorage and runs an AES decrypt without a
 * guard, and both throw — a private window or blocked site data on the read,
 * a store value that no longer decrypts on the parse. The language must not be
 * the reason the application fails to start, so a failure here means "nothing
 * stored" and the default stays in place.
 */
export const readStoredLocale = (): SupportedLocale | null => {
  try {
    const stored = useFilterStore().filter[LOCALE_STORE_KEY]

    return isSupportedLocale(stored) ? stored : null
  } catch (error) {
    console.error('[i18n] could not read the stored language', error)

    return null
  }
}

/**
 * Applies the stored language. Called once at startup.
 *
 * Nothing stored, or something invalid stored, leaves the locale that
 * createI18n() was given — it is not overwritten with a guess.
 */
export const applyStoredLocale = () => {
  const stored = readStoredLocale()

  if (stored) {
    writeLocaleToI18n(stored)
  }
}

/**
 * Switches the language and remembers the choice.
 *
 * An unsupported code is ignored rather than applied, so a caller cannot put
 * the interface into a locale that has no messages file. Storing is allowed to
 * fail (see readStoredLocale): the language then still changes for this
 * session, it just does not survive the next reload.
 */
export const setLocale = (code: string) => {
  if (!isSupportedLocale(code)) {
    console.error('[i18n] ignored an unsupported language', code)

    return
  }

  writeLocaleToI18n(code)

  try {
    useFilterStore().SET(LOCALE_STORE_KEY, code)
  } catch (error) {
    console.error('[i18n] could not store the language', error)
  }
}
